<!--
  AiPromptManagement.vue
  AI 프롬프트 관리 페이지
  
  주요 기능:
  - 프롬프트 목록 조회 및 관리
  - 프롬프트 추가/수정/삭제
  - 후기 생성 테스트
  - 프롬프트 타입별 필터링
  
  API 엔드포인트:
  - GET /reviews/prompts - 프롬프트 목록 조회
  - POST /reviews/prompts - 프롬프트 추가
  - PUT /reviews/prompts/:id - 프롬프트 수정
  - DELETE /reviews/prompts/:id - 프롬프트 삭제
  - POST /reviews/generate - 후기 생성 테스트
-->
<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>
    
    <!-- 페이지 헤더 영역 -->
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 justify-content-between titleContainer">
          <div class="title-row">
            <h1 class="fw-bold page-title">
              AI 프롬프트 관리
            </h1>
          </div>
          <span class="pageDesc">AI 후기 생성을 위한 프롬프트를 관리합니다.</span>
        </div>
      </div>
    </div>
    
    <!-- 콘텐츠 영역 -->
    <div class="px-0 pb-2">
      <!-- 프롬프트 관리 영역 -->
      <div class="card content-wrap-card">
        <!-- 검색 및 필터 컴포넌트 -->
        <PromptSearchForm
          :loading="state.searchLoading"
          @search="handleSearch"
          @reset="handleReset"
        />
        
        <!-- 테이블 컴포넌트 -->
        <PromptTable
          :prompt-list="promptList"
          :loading="state.tableLoading"
          :pagination="pagination"
          @add="handleAdd"
          @edit="handleEdit"
          @delete="handleDelete"
          @test="handleTest"
          @show-test="handleShowTest"
          @table-change="handleTableChange"
        />
        
        <!-- 디버깅 정보 -->
        <div v-show="false" style="margin-top: 20px; padding: 10px; background: #f0f0f0;">
          <h4>디버깅 정보:</h4>
          <p>promptList 길이: {{ promptList.length }}</p>
          <p>pagination.total: {{ pagination.total }}</p>
          <p>loading: {{ state.tableLoading }}</p>
          <pre>{{ JSON.stringify(promptList, null, 2) }}</pre>
        </div>
      </div>
    </div>
    
    <!-- 프롬프트 추가/수정 모달 컴포넌트 -->
    <PromptModal
      :open="state.modalVisible"
      :loading="state.modalLoading"
      :is-edit="state.isEdit"
      :prompt-data="currentPromptData"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
    />
    
    <!-- 후기 생성 테스트 모달 컴포넌트 -->
    <ReviewTestModal
      :open="state.testModalVisible"
      :loading="state.testLoading"
      :prompt-list="promptList"
      :generated-review="generatedReview"
      :selected-prompt="selectedTestPrompt"
      @ok="handleGenerateReview"
      @cancel="handleTestModalCancel"
      @prompt-select="handlePromptSelect"
    />
  </div>
</template>

<script>
import { ref, reactive, onMounted, getCurrentInstance } from 'vue';
import { message } from 'ant-design-vue';
import PromptSearchForm from './components/PromptSearchForm.vue';
import PromptTable from './components/PromptTable.vue';
import PromptModal from './components/PromptModal.vue';
import ReviewTestModal from './components/ReviewTestModal.vue';

export default {
  name: "AiPromptManagement",
  components: {
    PromptSearchForm,
    PromptTable,
    PromptModal,
    ReviewTestModal
  },
  setup() {
    // API 인스턴스 초기화
    const instance = getCurrentInstance();
    let apiInstance = null;
    
    if (instance && instance.proxy) {
      apiInstance = instance.proxy.$apiInstance;
      console.log('AI 프롬프트 관리 API 인스턴스 초기화:', apiInstance);
    } else {
      console.error('Current instance not available, API initialization failed');
    }

    // 반응형 상태 정의
    const state = reactive({
      searchLoading: false,
      tableLoading: false,
      modalVisible: false,
      modalLoading: false,
      testModalVisible: false,
      testLoading: false,
      isEdit: false,
      editingId: null
    });

    // 데이터 상태
    const promptList = ref([]);
    const generatedReview = ref('');
    const currentPromptData = ref({});
    const selectedTestPrompt = ref({});
    
    // 페이지네이션
    const pagination = reactive({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}개`
    });
    
    // 공통 에러 처리 함수
    const handleError = (error, defaultMessage) => {
      console.error('Error occurred:', error);
      
      let errorMessage = defaultMessage;
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
      return errorMessage;
    };

    // 프롬프트 목록 로드
    const loadPrompts = async (searchParams = {}) => {
      if (!apiInstance) {
        message.error('API가 초기화되지 않았습니다.');
        return;
      }
      
      state.tableLoading = true;
      
      try {
        const params = {
          page: pagination.current,
          pageSize: pagination.pageSize,
          ...searchParams
        };
        
        console.log('Loading prompts with params:', params); // 디버깅용
        
        const response = await apiInstance.get('/ai-prompts/prompts', { params });
        
        console.log('API Response:', response.data); // 디버깅용
        
        if (response.data) {
          // API 응답 구조에 따라 데이터 처리
          if (Array.isArray(response.data)) {
            // 응답이 배열인 경우
            promptList.value = response.data;
            pagination.total = response.data.length;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            // 응답이 객체이고 data 필드가 배열인 경우
            promptList.value = response.data.data;
            pagination.total = response.data.total || response.data.data.length;
            pagination.current = response.data.current || response.data.page || 1;
            pagination.pageSize = response.data.pageSize || response.data.limit || 10;
          } else {
            // 단일 객체인 경우 배열로 변환
            promptList.value = [response.data];
            pagination.total = 1;
          }
          
          console.log('Updated promptList:', promptList.value); // 디버깅용
          console.log('Updated pagination:', pagination); // 디버깅용
        }
      } catch (error) {
        handleError(error, '프롬프트 목록을 불러오는 중 오류가 발생했습니다.');
        promptList.value = [];
        pagination.total = 0;
      } finally {
        state.tableLoading = false;
      }
    };
    
    // 검색 처리
    const handleSearch = (searchParams) => {
      pagination.current = 1;
      
      // 백엔드 API 스펙에 맞는 검색 형식 처리
      const apiParams = {};
      
      if (searchParams.keyword && searchParams.keyword.trim()) {
        apiParams.searchField = searchParams.type;  // promptCode 또는 promptName
        apiParams.searchValue = searchParams.keyword.trim();
      }
      
      loadPrompts(apiParams);
    };
    
    // 검색 초기화
    const handleReset = () => {
      pagination.current = 1;
      loadPrompts();
    };
    
    // 프롬프트 추가
    const handleAdd = () => {
      state.isEdit = false;
      state.editingId = null;
      currentPromptData.value = {};
      state.modalVisible = true;
    };
    
    // 프롬프트 수정
    const handleEdit = (record) => {
      console.log('Editing record:', record); // 디버깅용
      state.isEdit = true;
      state.editingId = record.id;
      currentPromptData.value = { ...record };
      console.log('Current prompt data set to:', currentPromptData.value); // 디버깅용
      state.modalVisible = true;
    };
    
    // 프롬프트 삭제
    const handleDelete = async (id) => {
      if (!apiInstance) {
        message.error('API가 초기화되지 않았습니다.');
        return;
      }
      
      try {
        await apiInstance.delete(`/ai-prompts/prompts/${id}`);
        message.success('프롬프트가 성공적으로 삭제되었습니다.');
        loadPrompts();
      } catch (error) {
        handleError(error, '프롬프트 삭제 중 오류가 발생했습니다.');
      }
    };
    
    // 프롬프트 테스트
    const handleTest = (record) => {
      selectedTestPrompt.value = { ...record };
      state.testModalVisible = true;
    };
    
    // 테스트 모달 표시
    const handleShowTest = () => {
      selectedTestPrompt.value = {};
      generatedReview.value = '';
      state.testModalVisible = true;
    };
    
    // 모달 확인 처리
    const handleModalOk = async (formData) => {
      if (!apiInstance) {
        message.error('API가 초기화되지 않았습니다.');
        return;
      }
      
      state.modalLoading = true;
      
      try {
        if (state.isEdit) {
          await apiInstance.put(`/ai-prompts/prompts/${state.editingId}`, formData);
          message.success('프롬프트가 성공적으로 수정되었습니다.');
        } else {
          await apiInstance.post('/ai-prompts/prompts', formData);
          message.success('프롬프트가 성공적으로 추가되었습니다.');
        }
        
        state.modalVisible = false;
        loadPrompts();
      } catch (error) {
        handleError(error, state.isEdit ? '프롬프트 수정 중 오류가 발생했습니다.' : '프롬프트 추가 중 오류가 발생했습니다.');
      } finally {
        state.modalLoading = false;
      }
    };
    
    // 모달 취소 처리
    const handleModalCancel = () => {
      state.modalVisible = false;
      currentPromptData.value = {};
    };
    
    // 후기 생성
    const handleGenerateReview = async (testData) => {
      if (!apiInstance) {
        message.error('API가 초기화되지 않았습니다.');
        return;
      }
      
      console.log('Generating review with data:', testData); // 디버깅용
      
      state.testLoading = true;
      
      try {
        const response = await apiInstance.post('/ai-prompts/generate-review', testData);
        
        console.log('Generate review response:', response.data); // 디버깅용
        
        if (response.data?.reviewContent) {
          generatedReview.value = response.data.reviewContent;
          message.success('후기가 성공적으로 생성되었습니다.');
        } else if (response.data) {
          // API 응답 구조가 다를 수 있으므로 전체 응답을 확인
          generatedReview.value = response.data;
          message.success('후기가 성공적으로 생성되었습니다.');
        } else {
          message.error('후기 생성에 실패했습니다.');
        }
      } catch (error) {
        console.error('Generate review error:', error); // 디버깅용
        handleError(error, '후기 생성 중 오류가 발생했습니다.');
      } finally {
        state.testLoading = false;
      }
    };
    
    // 테스트 모달 취소 처리
    const handleTestModalCancel = () => {
      state.testModalVisible = false;
      selectedTestPrompt.value = {};
      generatedReview.value = '';
    };
    
    // 프롬프트 선택 처리
    const handlePromptSelect = (promptId) => {
      debugger;
      const selectedPrompt = promptList.value.find(p => p.id === promptId);
      if (selectedPrompt) {
        selectedTestPrompt.value = { ...selectedPrompt };
      }
    };
    
    // 테이블 변경 핸들러
    const handleTableChange = (pag, filters, sorter) => {
      if (pagination.current !== pag.current || pagination.pageSize !== pag.pageSize) {
        pagination.current = pag.current;
        pagination.pageSize = pag.pageSize;
        loadPrompts();
      }
    };

    // 컴포넌트 마운트 시 데이터 로드
    onMounted(() => {
      loadPrompts();
    });
    
    return {
      // 상태관리
      state,
      pagination,
      
      // 데이터
      promptList,
      generatedReview,
      currentPromptData,
      selectedTestPrompt,
      
      // 이벤트 핸들러
      handleSearch,
      handleReset,
      handleAdd,
      handleEdit,
      handleDelete,
      handleTest,
      handleShowTest,
      handleModalOk,
      handleModalCancel,
      handleGenerateReview,
      handleTestModalCancel,
      handlePromptSelect,
      handleTableChange
    };
  }
};
</script>

<style scoped lang="scss">
.container-wrap {
  padding: 0 16px;
}

.card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  border: none;
  overflow: hidden;
}

.titleContainer {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.page-title {
  color: #000;
  font-size: 1.8rem;
  margin-bottom: 0;
}

.pageDesc {
  color: #6c757d;
  font-size: 14px;
  margin-top: 8px;
}
</style> 